import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SmartphoneIcon, WatchIcon, HeadphonesIcon, BatteryChargingIcon, ZapIcon, Gamepad2Icon, MonitorIcon, ChevronRightIcon, LayoutGridIcon } from 'lucide-react';
import { categories } from '../../data/categories';
import { Button } from '../ui/Button';
const iconMap: Record<string, React.ReactNode> = {
  Smartphone: <SmartphoneIcon className="w-5 h-5" data-id="element-97" />,
  Watch: <WatchIcon className="w-5 h-5" data-id="element-98" />,
  Headphones: <HeadphonesIcon className="w-5 h-5" data-id="element-99" />,
  Battery: <BatteryChargingIcon className="w-5 h-5" data-id="element-100" />,
  Zap: <ZapIcon className="w-5 h-5" data-id="element-101" />,
  Gamepad2: <Gamepad2Icon className="w-5 h-5" data-id="element-102" />,
  Monitor: <MonitorIcon className="w-5 h-5" data-id="element-103" />
};
export function CategorySection() {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const activeCategory = categories.find(c => c.id === hoveredCategory);
  return <>
      {/* Desktop Sidebar (lg and up) */}
      <div className="hidden lg:block relative w-[280px] flex-shrink-0 z-40" onMouseLeave={() => setHoveredCategory(null)} data-id="element-104">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden h-full flex flex-col" data-id="element-105">
          {/* Header */}
          <div className="bg-gray-900 text-white py-3 px-4 flex items-center gap-3 font-semibold" data-id="element-106">
            <LayoutGridIcon className="w-5 h-5" data-id="element-107" />
            <span data-id="element-108">All Categories</span>
          </div>

          {/* Category List */}
          <div className="flex-1 py-2 relative" data-id="element-109">
            {categories.map(category => <div key={category.id} onMouseEnter={() => setHoveredCategory(category.id)} className="group" data-id="element-110">
                <Link to={`/products?category=${category.slug}`} className={`flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 transition-colors ${hoveredCategory === category.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-111">
                  <div className="flex items-center gap-3" data-id="element-112">
                    <span className={`${hoveredCategory === category.id ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'} transition-colors`} data-id="element-113">
                      {iconMap[category.icon]}
                    </span>
                    <span className="text-sm font-medium" data-id="element-114">{category.name}</span>
                  </div>
                  <ChevronRightIcon className={`w-4 h-4 transition-all ${hoveredCategory === category.id ? 'opacity-100 text-blue-600 translate-x-0' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-gray-400'}`} data-id="element-115" />
                </Link>
              </div>)}
          </div>
        </div>

        {/* Flyout Panel */}
        {activeCategory && <div className="absolute top-0 left-full ml-2 w-[400px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-left-2 duration-200" data-id="element-116">
            <div className="p-6" data-id="element-117">
              <div className="flex items-start justify-between mb-4" data-id="element-118">
                <div data-id="element-119">
                  <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-120">
                    {activeCategory.name}
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-121">
                    {activeCategory.productCount} Products
                  </span>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-600" data-id="element-122">
                  {iconMap[activeCategory.icon]}
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-6" data-id="element-123">
                {activeCategory.description}
              </p>

              <div className="relative h-48 rounded-lg overflow-hidden mb-6 group" data-id="element-124">
                <img src={activeCategory.image} alt={activeCategory.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-125" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-126" />
              </div>

              <Link to={`/products?category=${activeCategory.slug}`} data-id="element-127">
                <Button variant="primary" className="w-full" data-id="element-128">
                  Browse {activeCategory.name}
                </Button>
              </Link>
            </div>
          </div>}
      </div>

      {/* Mobile Horizontal Scroll (below lg) */}
      <div className="lg:hidden py-4 bg-white border-b border-gray-100" data-id="element-129">
        <div className="max-w-7xl mx-auto px-4" data-id="element-130">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-131">
            {categories.map(category => <Link key={category.id} to={`/products?category=${category.slug}`} className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded-full text-sm font-medium text-gray-700 transition-colors whitespace-nowrap border border-gray-100 flex-shrink-0" data-id="element-132">
                <span className="text-gray-500" data-id="element-133">{iconMap[category.icon]}</span>
                {category.name}
              </Link>)}
          </div>
        </div>
      </div>
    </>;
}